/// <reference path="../types/office.d.ts" />

import { ParsedContact, OutlookColor, OUTLOOK_COLORS } from '@/types/contact';
import { supabase } from '@/integrations/supabase/client';

/**
 * Microsoft Graph API service
 * Handles contact creation and duplicate checking via server-side proxy
 * All Graph API requests are proxied through secure Edge Functions
 */
export class GraphService {
  /**
   * Make proxied request to Microsoft Graph API
   * All tokens are stored server-side for security
   */
  private static async graphRequest(endpoint: string, method: string = 'GET', body?: any): Promise<any> {
    console.log('[GraphService] ========================================');
    console.log('[GraphService] === GRAPH API REQUEST VIA PROXY ===');
    console.log('[GraphService] ========================================');
    console.log('[GraphService] Endpoint:', endpoint);
    console.log('[GraphService] Method:', method);

    const { data, error } = await supabase.functions.invoke('graph-proxy', {
      body: {
        endpoint,
        method,
        body,
      },
    });

    if (error) {
      console.error('[GraphService] Proxy error:', error);
      
      // Handle re-authentication needed
      if (error.message?.includes('No Microsoft Graph token') || error.message?.includes('token expired')) {
        throw new Error(
          'Microsoft authentication required. Please scan the QR code or sign in with Microsoft to re-authenticate.'
        );
      }
      
      throw new Error(error.message || 'Graph API request failed');
    }

    return data;
  }

  /**
   * Helper function to clean empty values from contact data
   */
  private static cleanContactData(data: any): any {
    const cleaned: any = {};
    
    for (const [key, value] of Object.entries(data)) {
      // Skip undefined, null, empty strings
      if (value === undefined || value === null || value === '') continue;
      
      // Skip empty arrays
      if (Array.isArray(value) && value.length === 0) continue;
      
      // Handle nested objects (like businessAddress)
      if (typeof value === 'object' && !Array.isArray(value)) {
        const cleanedNested = this.cleanContactData(value);
        // Only add if nested object has properties
        if (Object.keys(cleanedNested).length > 0) {
          cleaned[key] = cleanedNested;
        }
        continue;
      }
      
      // Add valid value
      cleaned[key] = value;
    }
    
    return cleaned;
  }

  /**
   * Create contact via Graph API (proxied)
   */
  static async createContact(contact: ParsedContact & { notes?: string }): Promise<void> {
    // Ensure category exists with correct color BEFORE creating contact
    if (contact.categories && contact.categories.length > 0 && contact.categoryColor) {
      await this.ensureCategory(contact.categories[0], contact.categoryColor);
    }

    // Determine display name
    const displayName = contact.displayName || 
      [contact.givenName, contact.middleName, contact.surname]
        .filter(Boolean)
        .join(' ') || 
      contact.companyName || '';

    // Build Graph API contact object with all available fields
    const rawContact: any = {
      // Name
      title: contact.title,
      givenName: contact.givenName,
      middleName: contact.middleName,
      surname: contact.surname,
      suffix: contact.suffix,
      nickname: contact.nickname,
      displayName,
      yomiGivenName: contact.yomiGivenName,
      yomiSurname: contact.yomiSurname,
      
      // Business
      companyName: contact.companyName,
      yomiCompanyName: contact.yomiCompanyName,
      department: contact.department,
      jobTitle: contact.jobTitle,
      profession: contact.profession,
      officeLocation: contact.officeLocation,
      manager: contact.manager,
      assistantName: contact.assistantName,
      assistantPhone: contact.assistantPhone,
      
      // Email
      emailAddresses: (contact.emailAddresses || []).filter(e => e).map(email => ({
        address: email,
        name: displayName,
      })),
      
      // Phones
      businessPhones: contact.businessPhones?.filter(p => p),
      homePhones: contact.homePhones?.filter(p => p),
      otherPhones: contact.otherPhones?.filter(p => p),
      mobilePhone: contact.mobilePhone,
      carPhone: contact.carPhone,
      pager: contact.pager,
      callback: contact.callback,
      radioPhone: contact.radioPhone,
      ttytddPhone: contact.ttytddPhone,
      telex: contact.telex,
      companyMainPhone: contact.companyMainPhone,
      
      // Fax
      businessFax: contact.businessFax,
      homeFax: contact.homeFax,
      otherFax: contact.otherFax,
      
      // Web
      businessHomePage: contact.businessHomePage,
      personalWebPage: contact.personalWebPage,
      
      // IM
      imAddresses: contact.imAddresses?.filter(im => im),
      
      // Addresses
      businessAddress: contact.businessAddress,
      homeAddress: contact.homeAddress,
      otherAddress: contact.otherAddress,
      
      // Personal
      spouseName: contact.spouseName,
      generation: contact.generation,
      birthday: contact.birthday,
      anniversary: contact.anniversary,
      schools: contact.schools,
      hobby: contact.hobby,
      location: contact.location,
      
      // Meta
      personalNotes: contact.notes || undefined,
      categories: contact.categories && contact.categories.length > 0
        ? contact.categories 
        : undefined,
    };

    // Clean the contact data to remove empty values
    const graphContact = this.cleanContactData(rawContact);

    console.log('[GraphService] ========================================');
    console.log('[GraphService] === CREATING CONTACT VIA PROXY ===');
    console.log('[GraphService] ========================================');
    console.log('[GraphService] Contact data:', JSON.stringify(graphContact, null, 2));

    try {
      const data = await this.graphRequest('/me/contacts', 'POST', graphContact);
      console.log('[GraphService] ✅ CONTACT CREATED SUCCESSFULLY');
      console.log('[GraphService] Contact ID:', data.id);
    } catch (error) {
      console.error('[GraphService] Failed to create contact:', error);
      throw error;
    }
  }

  /**
   * Check for duplicate contact by email
   */
  static async checkDuplicate(email: string): Promise<boolean> {
    const emailQuery = encodeURIComponent(email);
    const endpoint = `/me/contacts?$filter=emailAddresses/any(e:e/address eq '${emailQuery}')&$select=id`;
    
    try {
      const data = await this.graphRequest(endpoint, 'GET');
      return data.value && data.value.length > 0;
    } catch (error) {
      console.error('[GraphService] Failed to check duplicate:', error);
      throw new Error('Failed to check for duplicate contacts');
    }
  }

  /**
   * Get all Outlook categories
   */
  static async getCategories(): Promise<Array<{ id: string; displayName: string; color: string }>> {
    try {
      const data = await this.graphRequest('/me/outlook/masterCategories', 'GET');
      return data.value || [];
    } catch (error) {
      console.error('[GraphService] Failed to get categories:', error);
      throw new Error('Failed to retrieve Outlook categories');
    }
  }

  /**
   * Ensure category exists with correct color
   */
  static async ensureCategory(categoryName: string, color: OutlookColor): Promise<void> {
    console.log('[GraphService] 🏷️  Ensuring category exists:', categoryName, 'with color:', color);
    
    try {
      // Get all existing categories
      const categoriesData = await this.graphRequest('/me/outlook/masterCategories', 'GET');
      const existingCategory = categoriesData.value.find(
        (cat: any) => cat.displayName === categoryName
      );

      if (existingCategory) {
        console.log('[GraphService] Category exists:', existingCategory.displayName, 'with color:', existingCategory.color);
        
        // Check if color needs updating
        if (existingCategory.color !== color) {
          console.log('[GraphService] ⚠️  Color mismatch! Current:', existingCategory.color, 'Expected:', color);
          console.log('[GraphService] Deleting and recreating category with correct color...');
          
          // Delete the category with wrong color
          try {
            await this.graphRequest(`/me/outlook/masterCategories/${existingCategory.id}`, 'DELETE');
            console.log('[GraphService] ✅ Old category deleted');
          } catch (deleteError: any) {
            if (!deleteError.message.includes('404')) {
              console.error('[GraphService] ❌ Failed to delete category:', deleteError);
              throw new Error('Failed to delete category');
            }
          }
          
          // Create category with correct color
          const createdCategory = await this.graphRequest('/me/outlook/masterCategories', 'POST', {
            displayName: categoryName,
            color: color,
          });
          console.log('[GraphService] ✅ Category recreated with correct color:', createdCategory.displayName, createdCategory.color);
        } else {
          console.log('[GraphService] ✅ Category color is already correct');
        }
      } else {
        console.log('[GraphService] Category does not exist, creating...');
        
        // Create new category
        try {
          const createdCategory = await this.graphRequest('/me/outlook/masterCategories', 'POST', {
            displayName: categoryName,
            color: color,
          });
          console.log('[GraphService] ✅ Category created:', createdCategory.displayName, createdCategory.color);
        } catch (createError: any) {
          // Check for PERMISSION_REQUIRED error
          if (createError.message.includes('PERMISSION_REQUIRED') || createError.message.includes('Forbidden')) {
            console.error('[GraphService] ❌ PERMISSION DENIED - Missing Mailbox.ReadWrite permission');
            throw new Error(
              'Missing permission to create Outlook categories. ' +
              'Please ensure the app has "Mailbox.ReadWrite" permission and the user has consented. ' +
              'Categories can only be created if the app has explicit permission to modify mailbox data.'
            );
          }
          throw createError;
        }
      }
    } catch (error) {
      console.error('[GraphService] Error ensuring category:', error);
      throw error;
    }
  }
}
